import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Card, Button } from 'react-bootstrap';
import IconButton from 'components/common/IconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CopyButton from '../servicio/CopyButton';

/**
 * UsuariosAkasiaPOS
 * Componente de presentación para usuarios Akasia POS
 * Solo UI, sin lógica de negocio (la lógica está en SeccionUsuariosAkasiaPOS)
 */
const UsuariosAkasiaPOS = ({ usuarios, loading, licencias, onNuevoUsuario, onEditarUsuario, onCrearSuperAdmin }) => {
    const resolveRoleName = (usuario) => usuario.vRolName || usuario.vNombreRol || usuario.vRoleName || (usuario.bIsSuperAdmin ? 'Super Admin' : 'Administrador');

    const formatDate = (d) => {
        if (!d) return '—';
        try {
            const dt = new Date(d);
            if (Number.isNaN(dt.getTime())) return d;
            const day = dt.getDate().toString().padStart(2, '0');
            const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
            const month = months[dt.getMonth()];
            const year = dt.getFullYear();
            return `${day} ${month} ${year}`;
        } catch {
            return d;
        }
    };

    const getStatusBadge = (isSuperAdmin, expirationDate) => {
        if (isSuperAdmin) {
            return { label: 'Activo', variant: 'success' };
        }
        if (!expirationDate) {
            return { label: 'Activo', variant: 'success' };
        }
        try {
            const now = new Date();
            const expDate = new Date(expirationDate);
            const diffTime = expDate - now;
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            
            if (diffDays < 0) {
                return { label: 'Vencido', variant: 'danger' };
            } else if (diffDays <= 7) {
                return { label: 'Por Vencer', variant: 'warning' };
            } else {
                return { label: 'Activo', variant: 'success' };
            }
        } catch {
            return { label: 'Activo', variant: 'success' };
        }
    };

    return (
        <Card className="mb-3">
            <Card.Body>
                <Row className="align-items-center">
                    <Col>
                        <h5 className="mb-0">Usuarios Akasia POS</h5>
                    </Col>
                    <Col xs="auto">
                        {/* Mostrar botón "Crear Super Admin" solo si NO hay usuarios */}
                        {usuarios.length === 0 && licencias?.bIsActive && (
                            <IconButton
                                onClick={onCrearSuperAdmin}
                                iconClassName="fs--2 me-1"
                                variant="falcon-primary"
                                size="sm"
                                icon="shield-alt"
                            >
                                Crear Super Admin
                            </IconButton>
                        )}
                        {/* Mostrar botón "Nuevo Usuario" solo si licencia está activa y pagada */}
                        {usuarios.length > 0 && licencias?.bIsActive && !licencias?.bIsPeriodoPrueba && (
                            <IconButton
                                onClick={onNuevoUsuario}
                                iconClassName="fs--2 me-1"
                                variant="falcon-default"
                                size="sm"
                                icon="pencil-alt"
                            >
                                Nuevo Usuario POS
                            </IconButton>
                        )}
                    </Col>
                </Row>
                <hr />

                <div>
                    {loading ? (
                        <div className="text-center">Cargando...</div>
                    ) : (
                        <div>
                            {usuarios.length === 0 ? (
                                <div className="text-muted">No hay usuarios registrados.</div>
                            ) : (
                                usuarios.map(u => (
                                    <Card key={u.idPosUser || u.idUsuarioAkasiaPos || u.idUsuarioNubeAkasia} className="mb-2 shadow-sm">
                                        <Card.Body>
                                            <Row>
                                                <Col xs={1} className="d-flex align-items-start justify-content-center">
                                                    <img src="/img/usuario20.png" alt="Usuario" />
                                                </Col>
                                                <Col md={7} xs={11}>
                                                    <div className="small mb-2 d-flex align-items-center gap-2">
                                                        <strong>Correo:</strong>
                                                        <span>{u.vEmail || '—'}</span>
                                                        {u.vEmail && <CopyButton code={u.vEmail} duration={1200} />}
                                                    </div>
                                                    <div className="small mb-2">
                                                        <strong>Usuario:</strong> {u.vUsername || u.vUUIDPosUser || u.idPosUser || '—'}
                                                    </div>
                                                    {u.vPassword && (
                                                        <div className="small mb-2 d-flex align-items-center gap-2">
                                                            <strong>Contraseña:</strong>
                                                            <code>{u.vPassword}</code>
                                                            <CopyButton code={u.vPassword} duration={1200} />
                                                        </div>
                                                    )}
                                                    <div className="small mb-1">
                                                        <strong>Fecha de registro:</strong> {formatDate(u.dCreatedDate || u.dStartDate)}
                                                    </div>
                                                    {!u.bIsSuperAdmin && (
                                                        <div className="small mb-1">
                                                            <strong>Fecha de Vencimiento:</strong> {formatDate(u.dFinishDate || u.dExpirationDate || u.dUpdatedDate)}
                                                        </div>
                                                    )}
                                                </Col>
                                                <Col md={4} xs={12} className="text-md-end d-flex flex-column align-items-end justify-content-between">
                                                    <div className="mb-2">
                                                        <span className="badge bg-primary me-1 mb-1">{resolveRoleName(u)}</span>
                                                        <span className={`badge bg-${getStatusBadge(u.bIsSuperAdmin, u.dFinishDate || u.dExpirationDate || u.dUpdatedDate).variant} mb-1`}>
                                                            {getStatusBadge(u.bIsSuperAdmin, u.dFinishDate || u.dExpirationDate || u.dUpdatedDate).label}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <Button variant="primary" size="sm" onClick={() => onEditarUsuario(u)}>
                                                            <FontAwesomeIcon icon="edit" className="me-1" aria-hidden="true" />Editar
                                                        </Button>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    </Card>
                                ))
                            )}
                        </div>
                    )}
                </div>
            </Card.Body>
        </Card>
    );
};

UsuariosAkasiaPOS.propTypes = {
    usuarios: PropTypes.arrayOf(
        PropTypes.shape({
            idPosUser: PropTypes.number,
            idUsuarioAkasiaPos: PropTypes.number,
            idUsuarioNubeAkasia: PropTypes.number,
            vEmail: PropTypes.string,
            vUsername: PropTypes.string,
            vUUIDPosUser: PropTypes.string,
            vPassword: PropTypes.string,
            vRolName: PropTypes.string,
            vNombreRol: PropTypes.string,
            vRoleName: PropTypes.string,
            bIsSuperAdmin: PropTypes.bool,
            dCreatedDate: PropTypes.string,
            dStartDate: PropTypes.string,
            dFinishDate: PropTypes.string,
            dExpirationDate: PropTypes.string,
            dUpdatedDate: PropTypes.string
        })
    ).isRequired,
    loading: PropTypes.bool.isRequired,
    licencias: PropTypes.shape({
        bIsActive: PropTypes.bool,
        bIsPeriodoPrueba: PropTypes.bool
    }),
    onNuevoUsuario: PropTypes.func.isRequired,
    onEditarUsuario: PropTypes.func.isRequired,
    onCrearSuperAdmin: PropTypes.func.isRequired
};

export default UsuariosAkasiaPOS;
